import { Badge, BasePagination, Text } from '@elkaso-app/web-design';
import { useGetSiSuppliersApi } from 'apis/supplier-intelligence';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, ClearFilterButton } from 'components/base-table/components';
import { TableHeader } from 'components/base-table/components/table-header';
import { BaseSortButton } from 'components/base-table/sorting/base-sort-button';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { useEffect, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { SupplierIsPublicFilterButton } from '../components/filters/supplier-is-public-filter-button';
import { SupplierSearchFilterButton } from '../components/filters/supplier-search-filter-button';
import { SupplierStatusFilterButton } from '../components/filters/supplier-status-filter-button';

const SupplierListScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const setPaginationParams = useSetPaginationParamsV1();
  const { data: apiData, isLoading, isFetching } = useGetSiSuppliersApi();
  const { pageCount, total, data: suppliersData } = apiData ?? {};
  const tableData = suppliersData ?? [];

  useEffect(() => {
    dispatch(changeTitle('Supplier Intelligence — Suppliers'));
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Supplier Name'
            filterButtonComponent={<SupplierSearchFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='name' />}
          />
        ),
        accessor: 'name',
        Cell: ({ value, row }: any) => (
          <Text
            color='blue.600'
            cursor='pointer'
            fontWeight='medium'
            _hover={{ textDecoration: 'underline' }}
            onClick={() => history.push(`${match.url}/${row.original.id}`)}>
            {value}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Status' filterButtonComponent={<SupplierStatusFilterButton />} />,
        accessor: 'status',
        Cell: ({ value }: any) => (
          <Badge colorScheme={value === 'active' ? 'green' : 'gray'} variant='subtle'>
            {value}
          </Badge>
        ),
      },
      {
        Header: <TableHeader name='Total Products' sortButtonComponent={<BaseSortButton sortBy='totalProducts' />} />,
        accessor: 'totalProducts',
      },
      {
        Header: <TableHeader name='Total Orders' sortButtonComponent={<BaseSortButton sortBy='totalOrders' />} />,
        accessor: 'totalOrders',
      },
      {
        Header: (
          <TableHeader
            name='Is Public'
            filterButtonComponent={<SupplierIsPublicFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='isPublic' />}
          />
        ),
        accessor: 'isPublic',
        Cell: ({ value }: any) => (
          <Badge colorScheme={value ? 'green' : 'gray'} variant='subtle'>
            {value ? 'Yes' : 'No'}
          </Badge>
        ),
      },
    ],
    [history, match.url]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Suppliers' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>
        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};

export default SupplierListScreen;
